class StickyAddToCart extends HTMLElement {
  constructor() {
    super();
    this.opts = {
      stickySelector: this.getAttribute('sticky-selector') || '#sticky-add-to-cart',
      triggerSelector: this.getAttribute('trigger-selector') || '#main-add-to-cart',
      visibleClass: this.getAttribute('visible-class') || 'visible',
      hiddenClass: this.getAttribute('hidden-class') || 'hidden',
      headerSelector: this.getAttribute('header-selector') || 'header.site-header, .shopify-section-header-sticky',
      offset: this.hasAttribute('offset') ? parseInt(this.getAttribute('offset')) : null,
      minScrollY: this.hasAttribute('min-scroll-y') ? parseInt(this.getAttribute('min-scroll-y')) : 60,
      mobileOnly: this.hasAttribute('mobile-only'),
      maxWidth: this.hasAttribute('max-width') ? parseInt(this.getAttribute('max-width')) : 990,
      debug: this.hasAttribute('debug'),
      formSelector: this.getAttribute('form-selector') || 'form[action*="/cart/add"]',
      mainButtonSelector: this.getAttribute('main-button-selector') || '#main-add-to-cart',
      stickyButtonSelector: this.getAttribute('sticky-button-selector') || '#sticky-add-btn'
    };

    this.stickyEl = null;
    this.triggerEl = null;
    this.form = null;
    this.mainBtn = null;
    this.stickyBtn = null;
  }

  connectedCallback() {
    console.log("connected")
    // מופעל אוטומטית כשמכניסים את האלמנט ל־DOM
    this._setup();
  }

  disconnectedCallback() {
    this.destroy();
  }

  _setup() {
    this.stickyEl = this.querySelector(this.opts.stickySelector) || document.querySelector(this.opts.stickySelector);
    this.triggerEl = document.querySelector(this.opts.triggerSelector);
    this.form = document.querySelector(this.opts.formSelector);
    this.mainBtn = document.querySelector(this.opts.mainButtonSelector);
    this.stickyBtn = document.querySelector(this.opts.stickyButtonSelector);

    if (!this.stickyEl) {
      console.warn('[StickyAddToCart] Sticky element not found:', this.opts.stickySelector);
      return;
    }

    if (this.opts.hiddenClass) this.stickyEl.classList.add(this.opts.hiddenClass);

    if (!this.triggerEl) {
      this._waitForTrigger();
      return;
    }

    this._init();
    this._bindStickyClick();
  }

  _bindStickyClick() {
    if (!this.stickyBtn) return;
    this.stickyBtn.addEventListener('click', (e) => {
      e.preventDefault();

      const form = document.querySelector(this.opts.formSelector);
      if (!form) {
        console.warn('[StickyAddToCart] Form not found');
        return;
      }

      const submitBtn = form.querySelector('button[type="submit"][name="add"], button[type="submit"]');
      if (submitBtn && !submitBtn.disabled) {
        submitBtn.click();
      } else {
        form.submit();
      }
    });
  }

  destroy() {
    this._disconnectIO();
    this._detachListeners();
  }

  _init() {
    if (this.opts.mobileOnly && window.innerWidth > this.opts.maxWidth) {
      this._hide();
    }
    this._computeRootMargin();
    this._setupObserver();
    this._attachListeners();
    this.stickyEl.dataset.stickyReady = 'true';
  }

  _waitForTrigger() {
    const timeoutMs = 3000;
    const start = performance.now();
    const check = () => {
      this.triggerEl = document.querySelector(this.opts.triggerSelector);
      if (this.triggerEl) {
        this._init();
        this._bindStickyClick();
        return;
      }
      if (performance.now() - start > timeoutMs) {
        console.warn('[StickyAddToCart] Trigger not found:', this.opts.triggerSelector);
        return;
      }
      requestAnimationFrame(check);
    };
    requestAnimationFrame(check);
  }

  _computeRootMargin() {
    if (Number.isFinite(this.opts.offset)) {
      this.rootMargin = `0px 0px -${this.opts.offset}px 0px`;
      return;
    }
    const header = document.querySelector(this.opts.headerSelector);
    const headerH = header ? Math.ceil(header.getBoundingClientRect().height) : 0;
    const buffer = 16;
    const margin = Math.max(0, headerH + buffer);
    this.rootMargin = `0px 0px -${margin}px 0px`;
  }

  _setupObserver() {
    if (!this.triggerEl) return;
    this._disconnectIO();
    this.io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting && window.scrollY > this.opts.minScrollY) {
            this._show();
          } else {
            this._hide();
          }
        }
      },
      { threshold: 0, rootMargin: this.rootMargin }
    );
    this.io.observe(this.triggerEl);
  }

  _disconnectIO() {
    if (this.io) {
      this.io.disconnect();
      this.io = null;
    }
  }

  _attachListeners() {
    this._onResize = this._throttle(() => {
      if (this.opts.mobileOnly) {
        if (window.innerWidth > this.opts.maxWidth) {
          this._hide();
          this._disconnectIO();
          return;
        } else if (!this.io) {
          this._computeRootMargin();
          this._setupObserver();
        }
      }
      this._computeRootMargin();
      this._setupObserver();
    }, 250);

    window.addEventListener('resize', this._onResize, { passive: true });
    window.addEventListener('orientationchange', this._onResize, { passive: true });
  }

  _detachListeners() {
    if (this._onResize) {
      window.removeEventListener('resize', this._onResize);
      window.removeEventListener('orientationchange', this._onResize);
      this._onResize = null;
    }
  }

  _show() {
    this.stickyEl.classList.add(this.opts.visibleClass);
    if (this.opts.hiddenClass) this.stickyEl.classList.remove(this.opts.hiddenClass);
  }

  _hide() {
    this.stickyEl.classList.remove(this.opts.visibleClass);
    if (this.opts.hiddenClass) this.stickyEl.classList.add(this.opts.hiddenClass);
  }

  _throttle(fn, wait = 200) {
    let last = 0, timer = null;
    return (...args) => {
      const now = Date.now();
      if (now - last >= wait) {
        last = now;
        fn.apply(this, args);
      } else if (!timer) {
        const remaining = wait - (now - last);
        timer = setTimeout(() => {
          last = Date.now();
          timer = null;
          fn.apply(this, args);
        }, remaining);
      }
    };
  }
}

customElements.define('sticky-add-to-cart', StickyAddToCart);
